import { RefObject, useRef, useState } from "react";
import { RouteFactory } from "@app/routePaths";
import {
  AdvertisementTypeEnum,
  NewsletterArchiveAdKeywordsEnums,
  NewsletterArchiveKeywordsEnums,
} from "@app/types/enums";
import { NewsletterArchiveContext } from "@app/types/Page";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import { ASEAN_BUSINESS_CONTRIBUTORS } from "@components/Newsletter/constants";
import HomepageCarouselNewsletter from "@components/Newsletter/HomepageCarouselNewsletter";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import AseanBusinessLatestIssue from "@pages/Newsletter/Archive/AseanBusiness/SampleIssues/AseanBusinessLatestIssue";
import NewsletterArchiveArticlesList from "@pages/Newsletter/Archive/components/NewsletterArchiveArticlesList";
import NewsletterArchiveHeader from "@pages/Newsletter/Archive/components/NewsletterArchiveHeader";
import SocialShare from "@pages/Newsletter/Archive/components/SocialShare";
import { ENVIRONMENT } from "@util/constant";
import { cn, getEnvironmentDomain, GoogleAdsSlotFactory } from "@util/helpers";

export type AseanBusinessArchivePageContentProps = Pick<
  NewsletterArchiveContext,
  "data"
>;

export default function AseanBusinessArchivePageContent({
  data: { title, overview },
}: AseanBusinessArchivePageContentProps): React.ReactElement {
  const { isPrestitialEnabled, topOverlayImpressions, topOverlayValidity } =
    useGlobalAdSetting();
  const divRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [isKeepReadingVisible, setIsKeepReadingVisible] = useState(true);

  const aseanNL = BT_NEWSLETTERS.filter(
    ({ type }) => type === NEWSLETTER_TYPE.ASEAN
  );
  const dynamicClasses = !isKeepReadingVisible ? "overflow-visible h-full" : "";

  const handleButtonClick = () => {
    setIsKeepReadingVisible(false);
  };

  const baseUrl = getEnvironmentDomain(ENVIRONMENT);
  const newsletterAseanBusinessLink =
    RouteFactory.newsletterAseanBusinessArchivePage.replace("/", "");

  return (
    <>
      <MetaTags
        title={title}
        description="Read more at The Business Times."
        slug={RouteFactory.newsletterAseanBusinessArchivePage}
      />

      <GAData title="ASEAN Business" level2="newsletter" />

      <Advertisement
        rootClassName="py-6"
        adUnitProps={{
          type: AdvertisementTypeEnum.LB1,
          slot: GoogleAdsSlotFactory.lb1(newsletterAseanBusinessLink),
        }}
      />

      <PrestitialAdWrapper
        slot={GoogleAdsSlotFactory.prestitial(newsletterAseanBusinessLink)}
        isPrestitialEnabled={isPrestitialEnabled}
        topOverlayImpressions={topOverlayImpressions}
        topOverlayValidity={topOverlayValidity}
      />

      <Header />

      <Container rootClassName="pt-5 !px-5">
        <div className="border-b-2 border-black">
          <NewsletterArchiveHeader
            newsletterTitle="ASEAN Business"
            newsletterDescription="Business insights centering on South-east Asia's fast-growing economies."
            newsletterContributors={ASEAN_BUSINESS_CONTRIBUTORS}
            isBigMoney={false}
            isSubscriber={false}
            newsletterRoute={RouteFactory.newsletterAseanBusinessArchivePage}
            newsletterAdKeyword={NewsletterArchiveAdKeywordsEnums.AseanBusiness}
            newsletterTime={aseanNL[0].updateTime}
            signUpNL={aseanNL[0]}
            divRef={divRef}
            displayNewsletterForm={true}
          />
        </div>

        <NewsletterArchiveArticlesList
          newsletterKeyword={NewsletterArchiveKeywordsEnums.AseanBusiness}
          articles={overview}
        />

        <div className="relative flex flex-col lg:hidden">
          <AseanBusinessLatestIssue
            divRef={divRef}
            firstChildClassName={dynamicClasses}
            ClassName={cn("h-[600px]", dynamicClasses)}
          />

          {isKeepReadingVisible ? (
            <div className="test absolute bottom-0 mb-[1px] ml-4 w-11/12 bg-nlArchiveGradient px-4 pb-4 pt-20">
              <button
                className="flex w-full flex-row
                items-start justify-center rounded-3xl border border-gray-850 py-3 lg:hidden"
                onClick={handleButtonClick}
              >
                Keep Reading
              </button>
            </div>
          ) : null}
        </div>

        <SocialShare
          shareUrl={baseUrl + RouteFactory.newsletterAseanBusinessArchivePage}
          rootClassName={"pt-12 block lg:hidden pb-20"}
        />

        <HomepageCarouselNewsletter
          newsletterToSkip={NEWSLETTER_TYPE.ASEAN}
          blockTitle="YOU MAY LIKE"
          rootClassName="border-t border-gray-250 md:border-black pt-3 mb-16"
        />
      </Container>

      <Advertisement
        adUnitProps={{
          type: AdvertisementTypeEnum.ABM,
          slot: GoogleAdsSlotFactory.abm(),
        }}
      />
    </>
  );
}
